import { baseApi } from './baseApi';

export const platformApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    search: build.query({
      query: (q) => `/search?q=${encodeURIComponent(q ?? '')}`,
      transformResponse: (resp) => resp.data,
    }),
    notifications: build.query({
      query: () => '/notifications',
      transformResponse: (resp) => resp.data,
      // Recompute when appointments, invoices or leads change (the badge counts Not Connected leads).
      providesTags: [{ type: 'Appointment', id: 'LIST' }, { type: 'Invoice', id: 'LIST' }, { type: 'Lead', id: 'LIST' }],
    }),
    // Any WhatsApp campaign currently mid-send — the topbar's "sending" indicator polls this on
    // its own short interval (separate from the 2-minute notifications poll, since "how many are
    // left" should feel live while a campaign is actually running).
    activeSends: build.query({
      query: () => '/notifications/active-sends',
      transformResponse: (resp) => resp.data,
    }),
    // Today's Task Engine (Action Center): "who do I contact today".
    todayTasks: build.query({
      query: () => '/tasks/today',
      transformResponse: (resp) => resp.data,
      // Every task source maps to one of these caches — refetch when any of them change.
      providesTags: [
        { type: 'Lead', id: 'LIST' }, { type: 'LeadFollowUp', id: 'LIST' },
        { type: 'Patient', id: 'LIST' }, { type: 'Appointment', id: 'LIST' }, { type: 'Invoice', id: 'LIST' },
        'TaskRule',
      ],
    }),
    // ── Hand-written tasks ──
    // The board's ManualTasks column lists these; everything else on the board is a live query and
    // lives in taskColumnsApi. Endpoint names are global across the injected api, so keep these
    // distinct from anything defined there.
    createTask: build.mutation({
      query: (body) => ({ url: '/tasks', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['ClinicTask'],
    }),
    updateTask: build.mutation({
      query: ({ id, ...body }) => ({ url: `/tasks/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['ClinicTask'],
    }),
    // Rule-based task creation: staff-defined conditions evaluated live by the tasks engine.
    taskRules: build.query({
      query: () => '/tasks/rules',
      transformResponse: (resp) => resp.data,
      providesTags: ['TaskRule'],
    }),
    createTaskRule: build.mutation({
      query: (body) => ({ url: '/tasks/rules', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['TaskRule'],
    }),
    toggleTaskRule: build.mutation({
      query: (id) => ({ url: `/tasks/rules/${id}/toggle`, method: 'PATCH' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['TaskRule'],
    }),
    deleteTaskRule: build.mutation({
      query: (id) => ({ url: `/tasks/rules/${id}`, method: 'DELETE' }),
      invalidatesTags: ['TaskRule'],
    }),
    activity: build.query({
      query: ({ page = 1, pageSize = 25, entityType } = {}) => {
        const p = new URLSearchParams();
        p.append('page', page);
        p.append('pageSize', pageSize);
        if (entityType) p.append('entityType', entityType);
        return `/activity?${p.toString()}`;
      },
      transformResponse: (resp) => resp.data,
    }),
    // Recycle bin (admin)
    deletedLeads: build.query({
      query: () => '/leads/deleted',
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Lead', id: 'DELETED' }],
    }),
    restoreLead: build.mutation({
      query: (id) => ({ url: `/leads/${id}/restore`, method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Lead', id: 'DELETED' }, { type: 'Lead', id: 'LIST' }],
    }),
    deletedPatients: build.query({
      query: () => '/patients/deleted',
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Patient', id: 'DELETED' }],
    }),
    restorePatient: build.mutation({
      query: (id) => ({ url: `/patients/${id}/restore`, method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'Patient', id: 'DELETED' }, { type: 'Patient', id: 'LIST' }],
    }),
  }),
});

export const {
  useSearchQuery, useLazySearchQuery,
  useNotificationsQuery,
  useActiveSendsQuery,
  useTodayTasksQuery,
  useCreateTaskMutation, useUpdateTaskMutation,
  useTaskRulesQuery, useCreateTaskRuleMutation, useToggleTaskRuleMutation, useDeleteTaskRuleMutation,
  useActivityQuery,
  useDeletedLeadsQuery, useRestoreLeadMutation,
  useDeletedPatientsQuery, useRestorePatientMutation,
} = platformApi;
